import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Compass,
  Filter,
  Trash2,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { KGViewer } from '@/components/KGViewer/KGViewer';
import { Button, Card, CardContent } from '@/components/ui';
import { kgApi, ontologyApi, type OntologyClassSummary } from '@/lib/api';
import type { KGEdge, KGNode } from '@/components/KGViewer/KGViewer';
import { cn } from '@/lib/cn';
import type { SubgraphResponse } from '@/types/api';

import { DetailPanel } from './DetailPanel';
import { EntitySearch } from './EntitySearch';

/**
 * Neo4j Browser–style graph explorer.
 *
 * - Search picks an entity → its 1-hop subgraph fills the canvas.
 * - Click a node → side panel loads the entity detail.
 * - Click a neighbour in the panel OR double-click a node on the canvas →
 *   that entity's 1-hop subgraph is merged into the canvas, preserving the
 *   positions of nodes already laid out.
 * - History stack remembers every focused IRI so the back/forward arrows
 *   walk through the exploration trail.
 * - The class chip strip on the left lets the user hide entire categories
 *   (e.g. drop all `Country` nodes while inspecting projects).
 */
export function GraphExplorer() {
  const { t } = useTranslation('studio');
  const queryClient = useQueryClient();
  const [nodes, setNodes] = useState<Map<string, KGNode>>(new Map());
  const [edges, setEdges] = useState<Map<string, KGEdge>>(new Map());
  const [history, setHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [expandedIris, setExpandedIris] = useState<Set<string>>(new Set());
  const [hiddenClasses, setHiddenClasses] = useState<Set<string>>(new Set());

  const selectedIri = historyIndex >= 0 ? history[historyIndex] ?? null : null;

  const merge = useCallback((sub: SubgraphResponse) => {
    setNodes((prev) => {
      const next = new Map(prev);
      for (const n of sub.nodes) {
        if (!next.has(n.id)) next.set(n.id, n);
      }
      return next;
    });
    setEdges((prev) => {
      const next = new Map(prev);
      for (const e of sub.edges) {
        if (!next.has(e.id)) next.set(e.id, e);
      }
      return next;
    });
    setExpandedIris((prev) => {
      const next = new Set(prev);
      next.add(sub.root);
      return next;
    });
  }, []);

  // Push a new focus onto the history stack (or jump within it if the user
  // re-selected something we already visited).
  const focusIri = useCallback(
    (iri: string) => {
      setHistory((prev) => {
        // If we land back on the current entry, no-op.
        if (prev[historyIndex] === iri) return prev;
        // If we navigated back and now branch, drop the forward tail.
        const trimmed = prev.slice(0, historyIndex + 1);
        return [...trimmed, iri];
      });
      setHistoryIndex((prev) => prev + 1);
    },
    [historyIndex],
  );

  const loadAndFocus = useCallback(
    async (iri: string) => {
      focusIri(iri);
      void queryClient.prefetchQuery({
        queryKey: ['explorer', 'detail', iri],
        queryFn: () => kgApi.entityDetail(iri),
      });
      if (expandedIris.has(iri)) return;
      const sub = await kgApi.subgraph(iri, 1, 150);
      merge(sub);
    },
    [focusIri, expandedIris, queryClient, merge],
  );

  const handleClear = useCallback(() => {
    setNodes(new Map());
    setEdges(new Map());
    setHistory([]);
    setHistoryIndex(-1);
    setExpandedIris(new Set());
    setHiddenClasses(new Set());
  }, []);

  // Deep-link support: `?focus=<iri>` (e.g. from the map popup) loads
  // and focuses that entity once on mount. The ref guard prevents
  // re-focusing if the user navigates within the explorer afterwards.
  const [searchParams] = useSearchParams();
  const focusedFromUrl = useRef<string | null>(null);
  useEffect(() => {
    const param = searchParams.get('focus');
    if (!param || focusedFromUrl.current === param) return;
    focusedFromUrl.current = param;
    void loadAndFocus(param);
  }, [searchParams, loadAndFocus]);


  const goBack = useCallback(() => {
    setHistoryIndex((prev) => Math.max(0, prev - 1));
  }, []);
  const goForward = useCallback(() => {
    setHistoryIndex((prev) => Math.min(history.length - 1, prev + 1));
  }, [history.length]);

  const visibleNodes = useMemo(() => Array.from(nodes.values()), [nodes]);
  const visibleEdges = useMemo(() => Array.from(edges.values()), [edges]);

  // Ontology hierarchy — drives the tristate tree filter on the left.
  // Fetched once and cached for the session; the explorer doesn't need
  // to know about ontology drafts, so the long staleTime is safe.
  const { data: ontologyClasses } = useQuery({
    queryKey: ['ontology', 'classes'],
    queryFn: () => ontologyApi.listClasses(undefined, 5000),
    staleTime: 10 * 60_000,
  });

  // Class hierarchy restricted to what's on the canvas + every ancestor.
  // Each tree node carries the leaf class names whose instances live
  // under it, so toggling a parent flips the right entries in the
  // KGViewer's `hiddenClassNames` set in one go.
  const classTree = useMemo(
    () => buildClassTree(visibleNodes, ontologyClasses ?? []),
    [visibleNodes, ontologyClasses],
  );

  const highlightedIds = selectedIri ? [selectedIri] : [];

  return (
    <div className="flex h-[calc(100vh-3.5rem)] flex-col p-3">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 text-h2 text-brand-900">
          <Compass className="h-5 w-5 text-accent" aria-hidden />
          {t('explorer.title')}
        </div>

        <div className="ml-3 flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={goBack}
            disabled={historyIndex <= 0}
            aria-label={t('explorer.back')}
            title={t('explorer.back')}
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={goForward}
            disabled={historyIndex >= history.length - 1}
            aria-label={t('explorer.forward')}
            title={t('explorer.forward')}
          >
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Button>
        </div>

        <div className="flex-1" />

        <EntitySearch onPick={(iri) => void loadAndFocus(iri)} />
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClear}
          disabled={nodes.size === 0}
        >
          <Trash2 className="mr-1 h-4 w-4" aria-hidden />
          {t('explorer.clear')}
        </Button>
      </div>

      <div className="grid h-full min-h-0 grid-cols-[220px_1fr_360px] gap-3">
        <Card className="overflow-auto">
          <CardContent className="p-2">
            <p className="mb-1 flex items-center gap-1 text-h4 text-slate-700">
              <Filter className="h-3.5 w-3.5" aria-hidden />
              {t('explorer.filterByClass')}
            </p>
            {classTree.roots.length === 0 && (
              <p className="text-small text-slate-500">{t('explorer.noClasses')}</p>
            )}
            <ul className="space-y-0.5">
              {classTree.roots.map((node) => (
                <ClassTreeRow
                  key={node.name}
                  node={node}
                  depth={0}
                  hidden={hiddenClasses}
                  setHidden={setHiddenClasses}
                />
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card className="flex flex-col overflow-hidden">
          <CardContent className="relative h-full p-0">
            {visibleNodes.length === 0 ? (
              <EmptyCanvas />
            ) : (
              <KGViewer
                nodes={visibleNodes}
                edges={visibleEdges}
                highlightedNodeIds={highlightedIds}
                onNodeClick={(iri) => void loadAndFocus(iri)}
                onNodeDoubleClick={(iri) => void loadAndFocus(iri)}
                hiddenClassNames={hiddenClasses}
                maxNodes={400}
              />
            )}
          </CardContent>
        </Card>

        <DetailPanel iri={selectedIri} onNeighbourClick={(iri) => void loadAndFocus(iri)} />
      </div>
    </div>
  );
}

function EmptyCanvas() {
  const { t } = useTranslation('studio');
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center text-slate-500">
      <Compass className="h-10 w-10 text-slate-300" aria-hidden />
      <p className="text-body">{t('explorer.empty.title')}</p>
      <p className="text-small">{t('explorer.empty.hint')}</p>
    </div>
  );
}

// ─── Class-hierarchy filter ─────────────────────────────────────

interface ClassTreeNode {
  /** Short local name — what KGNode.className stores. */
  name: string;
  /** Number of nodes on the canvas with exactly this className (0 for ancestor-only). */
  directCount: number;
  /** Direct child classes that also appear in the tree. */
  children: ClassTreeNode[];
  /** Every leaf-class name (with direct nodes on canvas) in this subtree, including this one. */
  leafNames: string[];
  /** Total node count across the subtree (= sum of directCount in leafNames). */
  totalCount: number;
}

function buildClassTree(
  nodes: { className: string }[],
  ontology: OntologyClassSummary[],
): { roots: ClassTreeNode[] } {
  if (nodes.length === 0) return { roots: [] };

  // Count nodes per leaf className present on the canvas.
  const counts = new Map<string, number>();
  for (const n of nodes) counts.set(n.className, (counts.get(n.className) ?? 0) + 1);

  // Index the ontology by local_name and capture parent local_names.
  const localOf = (iri: string): string => iri.split(/[#/]/).pop() ?? iri;
  const parentsOfName = new Map<string, string[]>();
  for (const c of ontology) {
    parentsOfName.set(
      c.local_name,
      c.parents.map(localOf).filter((p) => p && p !== c.local_name),
    );
  }

  // Walk every canvas leaf up through its ancestors. Single-parent
  // walk keeps the tree clean; multi-inheritance ontologies fall
  // back to first-parent which matches the OntologyBrowser convention.
  const relevant = new Set<string>(counts.keys());
  for (const start of Array.from(counts.keys())) {
    let current: string | undefined = start;
    const guard = new Set<string>();
    while (current) {
      if (guard.has(current)) break;
      guard.add(current);
      relevant.add(current);
      const parents = parentsOfName.get(current) ?? [];
      current = parents.find((p) => p && p !== current);
    }
  }

  // Build children-of from the relevant set, using ontology parents.
  const childrenOf = new Map<string, Set<string>>();
  for (const cls of relevant) {
    for (const p of parentsOfName.get(cls) ?? []) {
      if (!relevant.has(p)) continue;
      const bucket = childrenOf.get(p) ?? new Set();
      bucket.add(cls);
      childrenOf.set(p, bucket);
    }
  }

  // Roots = relevant classes whose parents are all outside the relevant set.
  const roots = Array.from(relevant).filter(
    (cls) => !(parentsOfName.get(cls) ?? []).some((p) => relevant.has(p)),
  );

  const visited = new Set<string>();
  function buildNode(name: string): ClassTreeNode {
    visited.add(name);
    const childNames = Array.from(childrenOf.get(name) ?? []).filter(
      (c) => !visited.has(c),
    );
    const children = childNames
      .map(buildNode)
      .sort((a, b) => b.totalCount - a.totalCount || a.name.localeCompare(b.name));
    const directCount = counts.get(name) ?? 0;
    const leafNames = directCount > 0 ? [name] : [];
    let totalCount = directCount;
    for (const c of children) {
      leafNames.push(...c.leafNames);
      totalCount += c.totalCount;
    }
    return { name, directCount, children, leafNames, totalCount };
  }

  const rootNodes = roots
    .map(buildNode)
    .sort((a, b) => b.totalCount - a.totalCount || a.name.localeCompare(b.name));
  return { roots: rootNodes };
}

interface ClassTreeRowProps {
  node: ClassTreeNode;
  depth: number;
  hidden: Set<string>;
  setHidden: React.Dispatch<React.SetStateAction<Set<string>>>;
}

function ClassTreeRow({ node, depth, hidden, setHidden }: ClassTreeRowProps) {
  // Open the top two levels by default — analysts can collapse if it
  // gets noisy.
  const [open, setOpen] = useState(depth < 1);
  const checkboxRef = useRef<HTMLInputElement>(null);

  const hiddenLeafCount = node.leafNames.filter((n) => hidden.has(n)).length;
  const allHidden = hiddenLeafCount === node.leafNames.length && node.leafNames.length > 0;
  const someHidden = hiddenLeafCount > 0 && !allHidden;
  const checked = !allHidden;

  // Native indeterminate isn't reachable through the checked prop —
  // mirror it via the ref so screen readers + UI both see the state.
  useEffect(() => {
    if (checkboxRef.current) checkboxRef.current.indeterminate = someHidden;
  }, [someHidden]);

  const toggle = () => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (allHidden) {
        // All hidden → reveal every leaf in this subtree.
        for (const n of node.leafNames) next.delete(n);
      } else {
        // Some or none hidden → hide every leaf.
        for (const n of node.leafNames) next.add(n);
      }
      return next;
    });
  };

  const hasKids = node.children.length > 0;

  return (
    <li>
      <div
        className="flex items-center gap-1 rounded px-1 py-0.5 hover:bg-slate-50"
        style={{ paddingLeft: 4 + depth * 12 }}
      >
        {hasKids ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-slate-400 hover:text-slate-700"
            aria-label={open ? 'Collapse' : 'Expand'}
          >
            {open ? (
              <ChevronDown className="h-3 w-3" aria-hidden />
            ) : (
              <ChevronRight className="h-3 w-3" aria-hidden />
            )}
          </button>
        ) : (
          <span className="inline-block w-3" aria-hidden />
        )}
        <input
          ref={checkboxRef}
          type="checkbox"
          checked={checked}
          onChange={toggle}
          className="h-3.5 w-3.5"
          aria-label={`Toggle ${node.name}`}
        />
        <span
          className={cn(
            'flex-1 truncate text-small',
            allHidden ? 'text-slate-400' : 'text-slate-700',
          )}
          title={node.name}
        >
          {node.name}
        </span>
        <span className="rounded bg-slate-100 px-1.5 text-small text-slate-500">
          {node.totalCount}
        </span>
      </div>
      {hasKids && open && (
        <ul className="space-y-0.5">
          {node.children.map((child) => (
            <ClassTreeRow
              key={child.name}
              node={child}
              depth={depth + 1}
              hidden={hidden}
              setHidden={setHidden}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
